import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { extractError } from "@/api/client";
import { UserPlus, Bike, User, KeyRound, ShieldCheck, CheckCircle2, FileText } from "lucide-react";
import BackButton from "@/components/BackButton";
import PhoneInput from "@/components/PhoneInput";
import PasswordInput from "@/components/PasswordInput";
import TermsModal from "@/components/TermsModal";

export default function Register() {
  const { register, user } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const asParam = sp.get("as");
  const inviteFromUrl = sp.get("invite") || "";
  const referralFromUrl = (sp.get("ref") || "").toUpperCase();
  const initialRole = asParam === "driver" ? "driver" : asParam === "restaurant" ? "restaurant" : "customer";
  const [role, setRole] = useState(initialRole);
  const [form, setForm] = useState({ name: "", phone: "", email: "", password: "" });
  const [invite, setInvite] = useState(inviteFromUrl);
  const [referralCode, setReferralCode] = useState(referralFromUrl);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  // Pending review — shown after a customer successfully signs up
  const [pending, setPending] = useState(null);
  // Terms & Conditions
  const [agreedTerms, setAgreedTerms] = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  useEffect(() => {
    if (user) {
      if (user.role === "driver") nav("/driver");
      else if (user.role === "admin") nav("/admin");
      else if (user.role === "restaurant") nav("/restaurant");
      else nav("/");
    }
  }, [user, nav]);


  const submit = async (e) => {
    e?.preventDefault();
    // Enforce T&C agreement on ALL new registrations (customer + driver).
    if (!agreedTerms) {
      setErr("يجب الموافقة على الشروط والأحكام قبل إنشاء الحساب");
      return;
    }
    // Restaurant / driver: invite-code flow.
    if (role === "restaurant" || role === "driver") {
      if (!invite.trim()) {
        setErr(role === "restaurant" ? "كود الدعوة مطلوب للتسجيل كمالك مطعم" : "كود الدعوة مطلوب للتسجيل كسائق");
        return;
      }
      if (!form.phone.trim()) {
        setErr("رقم الهاتف مطلوب");
        return;
      }
      setBusy(true); setErr("");
      const r = await register({ ...form, role, invite_code: invite.trim() });
      setBusy(false);
      if (!r.ok) setErr(r.error);
      return;
    }
    // Customer flow — instant signup, no OTP verification step.
    if (!form.phone || !form.name || form.password.length < 6) {
      setErr("يرجى تعبئة كل الحقول المطلوبة (كلمة المرور 6 أحرف على الأقل)");
      return;
    }
    setBusy(true); setErr("");
    const r = await register({
      ...form,
      role: "customer",
      referral_code: referralCode.trim() ? referralCode.trim().toUpperCase() : undefined,
    });
    setBusy(false);
    if (!r.ok) { setErr(r.error); return; }
    if (r.pending) {
      setPending({ identifier: r.identifier, message: r.message });
      return;
    }
    // Instant customer signup — user is authenticated; head to the shop list.
    nav("/", { replace: true });
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 gradient-warm relative">
      <div className="absolute top-4 right-4">
        <BackButton to="/login"/>
      </div>
      <div className="w-full max-w-md bg-white rounded-3xl talabya-shadow-soft p-8 animate-fade-up">
        <Link to="/welcome" className="flex items-center gap-2 mb-4">
          <img src="/icon-192.png" alt="طلبية" className="w-12 h-12 rounded-xl object-cover" style={{boxShadow:"0 3px 8px rgba(37,71,208,0.3)"}} />
          <div>
            <div className="font-black text-xl" style={{fontFamily:'Cairo'}}>طلبية</div>
          </div>
        </Link>
        {pending ? (
          <div data-testid="reg-pending-screen">
            <div className="flex items-center gap-2 text-primary-brand mb-2">
              <CheckCircle2 size={22}/>
              <h1 className="text-2xl font-black" style={{fontFamily:'Cairo'}}>حسابك قيد المراجعة</h1>
            </div>
            <p className="text-sm text-[var(--text-secondary)] mb-4 leading-relaxed">
              {pending.message}
            </p>
            <div className="p-4 rounded-xl bg-blue-50 border border-blue-200 text-sm space-y-2">
              <div className="flex items-start gap-2">
                <ShieldCheck size={16} className="text-primary-brand mt-0.5 shrink-0"/>
                <div>
                  <strong>الخطوات القادمة:</strong>
                  <ol className="mt-1 mr-4 list-decimal text-xs text-[var(--text-secondary)] space-y-0.5">
                    <li>سيراجع مدير طلبية طلبك خلال فترة قصيرة.</li>
                    <li>سيتواصل معك عبر الواتساب أو الاتصال ويعطيك <strong>كود التفعيل (6 أرقام)</strong>.</li>
                    <li>ادخل صفحة التفعيل أدناه وأدخل الكود لتفعيل حسابك.</li>
                  </ol>
                </div>
              </div>
            </div>
            {pending.identifier && (
              <div className="mt-3 text-xs text-[var(--text-secondary)]">
                معرّف حسابك: <span className="font-mono font-bold text-[var(--text-primary)]" dir="ltr">{pending.identifier}</span>
              </div>
            )}
            <Link to="/activate" className="mt-5 block w-full py-3 rounded-xl bg-primary-brand text-white text-center font-black text-base hover:opacity-90 talabya-shadow-float" data-testid="reg-goto-activate">
              لدي كود التفعيل — تفعيل الآن
            </Link>
            <Link to="/login" className="mt-2 block text-center text-sm text-primary-brand font-bold" data-testid="reg-goto-login">
              العودة لتسجيل الدخول
            </Link>
          </div>
        ) : (<>
        <h1 className="text-3xl font-black mb-1" style={{fontFamily:'Cairo'}}>إنشاء حساب</h1>
        <p className="text-sm text-[var(--text-secondary)] mb-6">انضم إلى عائلة طلبية</p>

        <div className="grid grid-cols-2 gap-2 mb-5 bg-[var(--surface-alt)] p-1 rounded-xl">
          <button type="button" onClick={() => { setRole("customer"); }}
            className={`py-2 rounded-lg text-xs font-bold flex items-center justify-center gap-1 transition ${role === "customer" ? "bg-white talabya-shadow-soft text-primary-brand" : "text-[var(--text-secondary)]"}`}
            data-testid="role-customer">
            <User size={14}/> زبون
          </button>
          <button type="button" onClick={() => { setRole("driver"); }}
            className={`py-2 rounded-lg text-xs font-bold flex items-center justify-center gap-1 transition ${role === "driver" ? "bg-white talabya-shadow-soft text-primary-brand" : "text-[var(--text-secondary)]"}`}
            data-testid="role-driver">
            <Bike size={14}/> سائق
          </button>
        </div>

        {role === "driver" && (
          <div className="mb-3 p-3 rounded-xl bg-amber-50 border border-amber-300 text-xs text-[var(--text-primary)] flex items-start gap-2">
            <KeyRound size={14} className="mt-0.5 text-amber-600"/>
            <div>يتطلب التسجيل كسائق كود دعوة من إدارة طلبية. تواصل مع الإدارة للحصول عليه.</div>
          </div>
        )}

        <form onSubmit={submit} className="flex flex-col gap-3">
          <>
            <input required placeholder="الاسم الكامل"
                className="px-4 py-3 rounded-xl border border-[var(--border)] bg-[var(--surface-alt)] focus:bg-white focus:border-primary-brand focus:outline-none text-right"
                value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="reg-name"/>
              <input required type="tel" inputMode="tel" dir="ltr" placeholder="رقم الهاتف (09XXXXXXXX)"
                className="hidden"
                value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                data-testid="reg-phone-hidden"/>
              <PhoneInput
                value={form.phone}
                onChange={(e164) => setForm({ ...form, phone: e164 })}
                dataTestid="reg-phone"
              />
              <input type="email" placeholder="البريد الإلكتروني (اختياري)"
                className="px-4 py-3 rounded-xl border border-[var(--border)] bg-[var(--surface-alt)] focus:bg-white focus:border-primary-brand focus:outline-none text-right"
                value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="reg-email"/>
              <PasswordInput required minLength={6} placeholder="كلمة المرور (6 أحرف على الأقل)"
                inputClassName="w-full px-4 py-3 rounded-xl border border-[var(--border)] bg-[var(--surface-alt)] focus:bg-white focus:border-primary-brand focus:outline-none"
                value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                testId="reg-password"/>
              {(role === "restaurant" || role === "driver") && (
                <input required placeholder="كود الدعوة (من الإدارة)"
                  className="px-4 py-3 rounded-xl border border-primary-brand/40 bg-white focus:border-primary-brand focus:outline-none uppercase tracking-widest text-center font-bold"
                  value={invite} onChange={(e) => setInvite(e.target.value.toUpperCase())}
                  data-testid="reg-invite"/>
              )}
              {role === "customer" && (
                <div>
                  <input placeholder="كود إحالة صديق (اختياري)"
                    className="w-full px-4 py-3 rounded-xl border border-emerald-300 bg-emerald-50/40 focus:bg-white focus:border-emerald-500 focus:outline-none uppercase tracking-widest text-center font-bold"
                    value={referralCode}
                    onChange={(e) => setReferralCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 8))}
                    data-testid="reg-referral"/>
                  {referralCode && (
                    <div className="text-[11px] text-emerald-700 mt-1 flex items-center gap-1">
                      <ShieldCheck size={11}/> ستحصل على 1 د.ل هدية ترحيب بعد أول طلب مكتمل
                    </div>
                  )}
                </div>
              )}
              {err && <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg" data-testid="reg-error">{err}</div>}

              {/* Terms & Conditions agreement */}
              <label className={`flex items-start gap-2 p-3 rounded-xl border-2 cursor-pointer transition ${agreedTerms ? "border-primary-brand bg-blue-50" : "border-[var(--border)] bg-[var(--surface-alt)] hover:border-primary-brand/50"}`}>
                <input type="checkbox" checked={agreedTerms}
                       onChange={(e) => setAgreedTerms(e.target.checked)}
                       className="mt-0.5 w-5 h-5 accent-primary-brand cursor-pointer shrink-0"
                       data-testid="reg-terms-check"/>
                <div className="text-xs leading-relaxed flex-1">
                  <span>أوافق على </span>
                  <button type="button" onClick={() => setShowTerms(true)}
                          className="font-black text-primary-brand underline underline-offset-2 hover:opacity-80 inline-flex items-center gap-1"
                          data-testid="reg-terms-open">
                    <FileText size={12}/> الشروط والأحكام
                  </button>
                  <span> الخاصة بتطبيق طلبية.</span>
                </div>
              </label>

              {role !== "customer" && (
                <button disabled={busy || !agreedTerms}
                  className="w-full py-3 rounded-xl bg-primary-brand text-white font-bold text-base hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 talabya-shadow-float"
                  data-testid="reg-submit">
                  <UserPlus size={18}/>
                  {busy ? "جاري التسجيل..." : "إنشاء حساب"}
                </button>
              )}

              {role === "customer" && (
                <button disabled={busy || !agreedTerms}
                  className="w-full py-3 rounded-xl bg-primary-brand text-white font-bold text-base hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 talabya-shadow-float"
                  data-testid="reg-submit">
                  <UserPlus size={18}/>
                  {busy ? "جاري إنشاء الحساب..." : "إنشاء حساب"}
                </button>
              )}
            </>
        </form>

        <div className="mt-6 text-center text-sm">
          <span className="text-[var(--text-secondary)]">لديك حساب؟ </span>
          <Link to="/login" className="font-bold text-primary-brand" data-testid="go-login">تسجيل الدخول</Link>
        </div>
        </>)}
      </div>
      {showTerms && <TermsModal onClose={() => setShowTerms(false)} onAccept={() => setAgreedTerms(true)}/>}
    </div>
  );
}
